<template>
    <!-- 100% complete state: dismissible banner -->
    <BaseCard v-if="allComplete && !dismissed" theme="muted" class="flex items-center justify-between">
        <div>
            <p class="nadia-subtitle text-ink">Profile complete!</p>
            <p class="scaffold-details-small text-ink-muted">Nadia now has everything she needs to coach you.</p>
        </div>
        <BaseButton theme="tertiary" size="sm" icon-only aria-label="Dismiss" @click="dismissComplete">
            <i class="bi bi-x-lg text-lg/none" aria-hidden="true" />
        </BaseButton>
    </BaseCard>

    <!-- Main "Complete Profile" card (hidden when all done) -->
    <div v-if="!allComplete && !dismissed" class="rounded-xl border border-border bg-surface-raised p-5 md:p-6 flex flex-col gap-3 fs-mask">
        <!-- Header -->
        <div class="flex items-center justify-between">
            <h2 class="display-small-serif text-ink">Complete Profile</h2>
            <BaseTooltip side="bottom" trigger-aria-label="See how this information is used">
                <template #trigger>
                    <BaseButton theme="tertiary" size="sm" icon-only aria-label="See how this information is used" @click="emit('show-carousel')">
                        <i class="bi bi-info-circle text-lg" aria-hidden="true" />
                    </BaseButton>
                </template>
                <template #content>See how this information is used</template>
            </BaseTooltip>
        </div>
        <HorizontalDivider class="my-0" />

        <!-- Progress bar -->
        <div class="flex flex-col gap-3 py-2.5">
            <BaseProgressBar :value="completedSteps" :max="totalSteps" size="sm" aria-label="Profile completion progress" />
            <div class="flex justify-between items-center">
                <p class="scaffold-body-small text-ink-muted">Each step gives Nadia more to coach you with.</p>
                <p class="scaffold-details-small text-ink-muted">{{ completedSteps }} of {{ totalSteps }} steps</p>
            </div>
        </div>

        <!-- Featured Calendar CTA -->
        <BaseCard
            v-if="profileCompletion.calendar_enabled && !profileCompletion.has_calendar"
            theme="muted"
            class="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
            <div class="flex items-start gap-3">
                <CalendarStarIcon class="shrink-0 text-ink-muted" size="sm" />
                <div>
                    <p class="nadia-chat-bold text-ink">Connect your calendar</p>
                    <p class="scaffold-body-small text-ink-muted">Nadia can coach around your real meetings instead of guessing.</p>
                </div>
            </div>
            <BaseButton theme="primary" class="shrink-0 self-start md:self-center" @click="openCalendarSettings"> Connect Calendar </BaseButton>
        </BaseCard>
        <!-- Calendar connected state -->
        <BaseCard v-else-if="profileCompletion.calendar_enabled && profileCompletion.has_calendar" theme="muted" class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-3">
                <CalendarStarIcon class="text-ink-muted" size="sm" />
                <p class="body-semibold text-ink-muted line-through">Calendar connected</p>
            </div>
            <i class="bi bi-check-circle-fill text-lg/none text-chip-status-done-text shrink-0" aria-hidden="true" />
        </BaseCard>
        <CalendarIntegrationSettings
            v-if="profileCompletion.calendar_enabled && !profileCompletion.has_calendar"
            v-model:open="showCalendarSettings"
            source="coaching_journey"
            @close="showCalendarSettings = false" />

        <!-- Accordion Sections -->
        <BaseAccordion :accordion-ids="visibleSectionIds" :default-value="initiallyOpenSectionIds" class="journey-accordion">
            <template v-for="section in visibleSections" :key="section.id" #[`trigger-${section.id}`]>
                <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -- div is inside AccordionTrigger button; keyboard events handled by parent -->
                <div class="flex flex-wrap items-center justify-between flex-1 gap-2" @click="logSectionToggle(section.title)">
                    <div class="flex items-center gap-2 md:gap-3" :class="{ 'opacity-50': section.complete }">
                        <component :is="section.icon" class="h-3.5 w-6 shrink-0" />
                        <p class="body-semibold text-ink" :class="{ 'line-through': section.complete }">{{ section.title }}</p>
                    </div>
                    <div class="flex items-center gap-2">
                        <i v-if="section.complete" class="bi bi-check-circle-fill text-lg/none text-chip-status-done-text shrink-0" aria-hidden="true" />
                        <p class="scaffold-details-small text-ink-muted">{{ section.completed_count }} of {{ section.total_count }}</p>
                    </div>
                </div>
            </template>
            <template v-for="section in visibleSections" :key="section.id" #[`content-${section.id}`]>
                <div class="border-t border-border mx-5"></div>
                <div class="px-5 pb-5 flex flex-col gap-1">
                    <div
                        v-for="action in section.actions"
                        :key="action.label"
                        class="group/action flex items-center justify-between gap-3 py-2"
                        @vue:mounted="trackChecklistViewed()">
                        <div class="flex items-start gap-2">
                            <i v-if="action.isComplete" class="bi bi-check-circle-fill text-lg/none text-chip-status-done-text shrink-0 mt-1" aria-hidden="true" />
                            <i v-else class="bi bi-circle text-lg/none text-ink-muted shrink-0 mt-1" aria-hidden="true" />
                            <span class="flex flex-col">
                                <span class="scaffold-body-small" :class="action.isComplete ? 'text-ink-muted line-through' : 'text-ink'">
                                    <span v-if="action.isComplete" class="sr-only">Completed: </span>
                                    {{ action.label }}
                                </span>
                                <span v-if="!action.isComplete" class="details text-ink-muted">{{ action.description }}</span>
                                <span v-if="action.startsAssessment && startAssessmentError" role="alert" class="details text-button-danger">{{ startAssessmentError }}</span>
                            </span>
                        </div>
                        <!-- "Start" button on every incomplete item; always visible on the next step, hover-only otherwise -->
                        <a
                            v-if="!action.isComplete && action.isExternal"
                            :href="action.href"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="base-btn-primary base-btn-sm shrink-0"
                            :class="startButtonVisibility(action)"
                            :aria-label="`${START_TEXT} ${action.label}`"
                            @click="logStartClick(action)">
                            {{ START_TEXT }}
                        </a>
                        <BaseButton
                            v-else-if="!action.isComplete && (action.isHashNav || action.isCalendar)"
                            theme="primary"
                            size="sm"
                            class="shrink-0"
                            :class="startButtonVisibility(action)"
                            @click="action.isCalendar ? handleCalendarStepClick(action) : handleHashNavClick(action)">
                            Start
                        </BaseButton>
                        <BaseButton
                            v-else-if="!action.isComplete && action.startsAssessment"
                            theme="primary"
                            size="sm"
                            class="shrink-0"
                            :class="startButtonVisibility(action)"
                            :disabled="isStartingAssessment"
                            @click="handleStartAssessmentClick(action)">
                            Start
                        </BaseButton>
                        <BaseLink
                            v-else-if="!action.isComplete"
                            :href="action.href"
                            class="base-btn-primary base-btn-sm shrink-0"
                            :class="startButtonVisibility(action)"
                            :aria-label="`${START_TEXT} ${action.label}`"
                            @click="logStartClick(action)">
                            {{ START_TEXT }}
                        </BaseLink>
                    </div>
                </div>
            </template>
        </BaseAccordion>
    </div>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import CalendarIntegrationSettings from "~vue/components/CalendarIntegration/CalendarIntegrationSettings.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import CalendarStarIcon from "~vue/icons/CalendarStarIcon.vue";
import TileGreen from "~vue/icons/tiles/TileGreen.vue";
import TilePink from "~vue/icons/tiles/TilePink.vue";
import TileTeal from "~vue/icons/tiles/TileTeal.vue";
import { useOpenPerspectiveSession } from "~vue/Perspective/composable/useOpenPerspectiveSession";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames.ts";
import { logUserInteraction } from "~vue/utils/logUtils.ts";
import confetti from "canvas-confetti";
import { type Component, computed, onMounted, ref } from "vue";

import type { ProfileCompletionSummary } from "./types";

const START_TEXT = "Start";
let checklistViewLogged = false;
function trackChecklistViewed(): void {
    if (checklistViewLogged) {
        return;
    }
    checklistViewLogged = true;
    void logUserInteraction(AmplitudeEventNames.PRIVATE_PROFILE_CHECKLIST_VIEWED);
}

type CoachingJourneyAccordionProps = {
    profileCompletion: ProfileCompletionSummary;
};

const { profileCompletion } = defineProps<CoachingJourneyAccordionProps>();

type Section = {
    id: string;
    title: string;
    icon: Component;
    complete: boolean;
    completed_count: number;
    total_count: number;
    actions: {
        analyticsAction: string;
        label: string;
        description: string;
        href: string;
        isComplete: boolean;
        isHashNav: boolean;
        isExternal: boolean;
        startsAssessment: boolean;
        isCalendar: boolean;
    }[];
};

const emit = defineEmits<{
    "select-tab": [tabId: string];
    "show-carousel": [];
}>();

const iconMap: Record<string, Component> = {
    "getting-to-know-you": TilePink,
    "getting-to-know-your-team": TileGreen,
    "understanding-how-you-work": TileTeal,
};

const sections = computed<Section[]>(() =>
    profileCompletion.sections.map((s) => ({
        ...s,
        icon: iconMap[s.id] ?? TilePink,
        actions: s.actions.map((a) => ({
            analyticsAction: a.analytics_action ?? a.label,
            label: a.label,
            description: a.description,
            href: a.href,
            isComplete: a.is_complete,
            isHashNav: a.is_hash_nav,
            isExternal: a.is_external,
            startsAssessment: a.starts_assessment,
            isCalendar: a.is_calendar,
        })),
    })),
);

const visibleSections = computed(() => sections.value.filter((s) => s.actions.length > 0));
const visibleSectionIds = computed(() => visibleSections.value.map((s) => s.id));
const firstIncompleteSection = visibleSections.value.find((s) => !s.complete);
const initiallyOpenSectionIds = firstIncompleteSection ? [firstIncompleteSection.id] : [];

const calendarIsNextStep = profileCompletion.calendar_enabled && !profileCompletion.has_calendar;
const nextActionLabel = computed(() => (calendarIsNextStep ? undefined : visibleSections.value.flatMap((s) => s.actions).find((a) => !a.isComplete)?.label));
const startButtonVisibility = (action: Section["actions"][number]) =>
    action.label === nextActionLabel.value ? "" : "md:opacity-0 md:group-hover/action:opacity-100 md:transition-opacity";

const totalSteps = computed(() => sections.value.reduce((sum, s) => sum + s.total_count, 0));
const completedSteps = computed(() => sections.value.reduce((sum, s) => sum + s.completed_count, 0));
const allComplete = profileCompletion.all_complete;

const showCalendarSettings = ref(false);

const openCalendarSettings = () => {
    showCalendarSettings.value = true;
    logUserInteraction(AmplitudeEventNames.COACHING_JOURNEY_CALENDAR_CTA_CLICKED, {
        total: totalSteps.value,
        completed: completedSteps.value,
        location: "private_profile",
    });
};

const logSectionToggle = (sectionTitle: string) => {
    logUserInteraction(AmplitudeEventNames.COACHING_JOURNEY_ACCORDION_SECTION_TOGGLED, {
        section: sectionTitle,
        total: totalSteps.value,
        completed: completedSteps.value,
        location: "private_profile",
    });
};

const logStartClick = (action: Section["actions"][number]) => {
    logUserInteraction(AmplitudeEventNames.COACHING_JOURNEY_ACCORDION_STEP_CLICKED, {
        action: action.analyticsAction,
        total: totalSteps.value,
        completed: completedSteps.value,
        location: "private_profile",
    });
};

const handleHashNavClick = (action: Section["actions"][number]) => {
    logStartClick(action);
    emit("select-tab", action.href.split("#tab=")[1]);
};

const { openSession, isOpening: isStartingAssessment, errorMessage: startAssessmentError } = useOpenPerspectiveSession();

const handleStartAssessmentClick = async (action: Section["actions"][number]) => {
    logStartClick(action);
    if (await openSession()) {
        router.visit(action.href);
    }
};

const handleCalendarStepClick = (action: Section["actions"][number]) => {
    logStartClick(action);
    showCalendarSettings.value = true;
};

const dismissed = ref(profileCompletion.journey_complete_dismissed);
const dismissPayload = ref(null as { interaction_type: string } | null);
const { execute: saveDismissal } = useFetch("/api/accounts/ui-interactions", { immediate: false }).post(dismissPayload).json();

const dismissComplete = async () => {
    dismissed.value = true;
    dismissPayload.value = { interaction_type: "coaching_journey_complete_dismissed" };
    await saveDismissal();
    dismissPayload.value = null;
    fireConfetti();
};

const fireConfetti = () => {
    const defaults: confetti.Options = { particleCount: 50, spread: 70, ticks: 120, gravity: 1.2, scalar: 1.1, disableForReducedMotion: true };
    confetti({ ...defaults, angle: 60, origin: { x: 0, y: 1 } });
    confetti({ ...defaults, angle: 120, origin: { x: 1, y: 1 } });
    confetti({ ...defaults, particleCount: 80, spread: 100, origin: { x: 0.5, y: 1 } });
};

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.COACHING_JOURNEY_ACCORDION_VIEWED, {
        total: totalSteps.value,
        completed: completedSteps.value,
        all_complete: allComplete,
        location: "private_profile",
    });
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.journey-accordion {
    @apply flex flex-col gap-3;

    &:deep(.item) {
        @apply rounded-xl border border-border bg-surface-raised;
    }

    &:deep(.item[data-state="open"]) {
        @apply bg-card-surface-raised;
    }

    &:deep(.trigger) {
        @apply p-5;
    }
}
</style>
